/**
 * WCAG / VPAT Documents Section
 *
 * Owner/Admin management panel for the two client-facing accessibility
 * artifacts (VPAT and WCAG 2.1 AA audit report). Uploads use PDF only
 * and REPLACE any previous document of the same type — clients always
 * see the current version.
 *
 * Renders inline on AccessibilityPage.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Upload, Download, Trash2, FileText, Loader2 } from 'lucide-react';
import { Card, Badge } from '../ui-custom/ComplianceUI';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function WcagDocumentsSection() {
  const [docs, setDocs] = useState([]);
  const [supportedTypes, setSupportedTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadType, setUploadType] = useState('vpat');
  const [uploadTitle, setUploadTitle] = useState('');
  const fileInputRef = useRef(null);

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/wcag-documents`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setDocs(data.documents || []);
      setSupportedTypes(data.supported_types || {});
    } catch (e) {
      toast.error(`Failed to load WCAG documents: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleUpload = async () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) {
      toast.error('Choose a PDF file first');
      return;
    }
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are accepted');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('doc_type', uploadType);
      fd.append('title', uploadTitle);
      const res = await fetch(`${API_URL}/api/wcag-documents/upload`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      toast.success(
        data.document.replaced_doc_id
          ? `Replaced previous ${supportedTypes[uploadType] || uploadType}`
          : `Uploaded ${data.document.title}`
      );
      setUploadTitle('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchDocs();
    } catch (e) {
      toast.error(`Upload failed: ${e.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await fetch(`${API_URL}/api/wcag-documents/${doc.doc_id}/download`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(`Download failed: ${e.message}`);
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.title}"? Clients will lose access immediately.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/wcag-documents/${doc.doc_id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success(`Deleted ${doc.title}`);
      fetchDocs();
    } catch (e) {
      toast.error(`Delete failed: ${e.message}`);
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <div className="wcag-docs-section" data-testid="wcag-documents-section" style={{ marginTop: 40 }}>
      <h2 className="section-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <FileText size={20} /> Client-Facing Accessibility Documents
      </h2>
      <p style={{ color: '#94a3b8', marginBottom: 20, fontSize: 14 }}>
        VPAT and WCAG audit reports that clients (with the Accessibility Documents scope) can download
        from their portal. New uploads <b>replace</b> the previous version of the same type.
      </p>

      {/* Upload panel */}
      <Card style={{ padding: 20, marginBottom: 24 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr auto', gap: 12, alignItems: 'center' }}>
          <label style={{ fontSize: 13, color: '#94a3b8' }}>Document type</label>
          <select
            value={uploadType}
            onChange={(e) => setUploadType(e.target.value)}
            data-testid="wcag-doc-type-select"
            style={{
              padding: '8px 10px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.15)',
              background: 'rgba(255,255,255,0.05)', color: 'inherit',
            }}
          >
            {Object.entries(supportedTypes).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
          <span />

          <label style={{ fontSize: 13, color: '#94a3b8' }}>Title (optional)</label>
          <input
            type="text"
            placeholder="e.g. Eyvo Procurement VPAT v2.4 — Feb 2026"
            value={uploadTitle}
            onChange={(e) => setUploadTitle(e.target.value)}
            data-testid="wcag-doc-title-input"
            style={{
              padding: '8px 10px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.15)',
              background: 'rgba(255,255,255,0.05)', color: 'inherit',
            }}
          />
          <span />

          <label style={{ fontSize: 13, color: '#94a3b8' }}>PDF file</label>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,application/pdf"
            data-testid="wcag-doc-file-input"
            style={{ color: 'inherit', fontSize: 13 }}
          />
          <button
            onClick={handleUpload}
            disabled={uploading}
            data-testid="wcag-doc-upload-btn"
            style={{
              padding: '9px 16px', borderRadius: 6,
              background: uploading ? '#334155' : '#2563eb',
              color: '#fff', border: 'none', cursor: uploading ? 'wait' : 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            {uploading ? <Loader2 className="spin" size={16} /> : <Upload size={16} />}
            {uploading ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </Card>

      {/* Documents list */}
      {loading ? (
        <div style={{ padding: 30, textAlign: 'center' }}><Loader2 className="spin" size={20} /></div>
      ) : docs.length === 0 ? (
        <Card style={{ padding: 30, textAlign: 'center', color: '#94a3b8' }} data-testid="wcag-docs-empty">
          No WCAG/VPAT documents uploaded yet. Upload the first one above.
        </Card>
      ) : (
        <div className="wcag-docs-list" data-testid="wcag-docs-list">
          {docs.map((doc) => (
            <Card
              key={doc.doc_id}
              data-testid={`wcag-doc-${doc.doc_type}`}
              style={{
                padding: 16, marginBottom: 12,
                display: 'flex', alignItems: 'center', gap: 16,
              }}
            >
              <FileText size={24} color="#2563eb" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, marginBottom: 2 }}>{doc.title}</div>
                <div style={{ fontSize: 12, color: '#94a3b8' }}>
                  {doc.doc_type_label} · {doc.filename} · {formatSize(doc.file_size_bytes)}
                  {' · '}Uploaded {new Date(doc.uploaded_at).toLocaleDateString()} by {doc.uploaded_by}
                </div>
              </div>
              <Badge color="#2563eb">{doc.doc_type.toUpperCase().replace('_', ' ')}</Badge>
              <button
                onClick={() => handleDownload(doc)}
                data-testid={`wcag-doc-download-${doc.doc_id}`}
                title="Download"
                style={{
                  background: 'transparent', border: '1px solid rgba(255,255,255,0.15)',
                  color: 'inherit', padding: '6px 10px', borderRadius: 6, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                <Download size={14} /> Download
              </button>
              <button
                onClick={() => handleDelete(doc)}
                data-testid={`wcag-doc-delete-${doc.doc_id}`}
                title="Delete"
                style={{
                  background: 'transparent', border: '1px solid rgba(255,60,60,0.3)',
                  color: '#f87171', padding: '6px 10px', borderRadius: 6, cursor: 'pointer',
                  display: 'flex', alignItems: 'center',
                }}
              >
                <Trash2 size={14} />
              </button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
